// Design System
import env from 'jl-design-system/utils/env/env';

// Checkout Forms
import validate from 'checkout-forms/form/validation/validate';

// Config
import { updateBillingAddress } from './cardPaymentActions';
import {
  handleApplePayExpress,
  handleExpressFailure,
  setPartialDeliveryAddress,
  cleanupExpressBasket,
} from './expressPaymentActions';
import { initPaymentPage } from './orderFormActions';
import {
  setPaymentProcessing,
  triggerTransactionStatusAnalytics,
  triggerPlaceOrderAndPayAnalytics,
} from './paymentActions';
import { submitOrderAndRedirect } from './submitOrderActions';
import {
  ANALYTICS_OPEN_APPLE_PAY_PAYMENT_SHEET,
  APPLE_PAY_BILLING_ADDRESS_MISSING,
  APPLE_PAY_PAYMENT,
  APPLE_PAY_PAYMENT_CANCEL,
  APPLE_PAY_PAYMENT_FAILURE,
  APPLE_PAY_REQUEST_PAYMENT_SESSION,
  APPLE_PAY_SET_CARD_TYPE,
  APPLE_PAY_SUBMIT_PAYMENT_DATA,
  SET_APPLE_PAY_PAYMENT_REQUEST,
  SET_CAN_MAKE_APPLE_PAY_PAYMENTS,
  UPDATE_BILLING_ADDRESS,
  EXPRESS_PAYMENTS_VALIDATION_FAILED,
  ANALYTICS_OPEN_APPLE_PAY_EXPRESS_PAYMENT_SHEET,
  GET_APPLE_PAY_EXPRESS_PAYMENT_REQUEST,
  SET_APPLE_PAY_ENHANCED_SHIPPING_IDENTIFIER,
  EXPRESS_PAYMENTS_FAILED,
  APPLE_PAY_CANCEL,
} from '@constants/actionConstants';
import {
  URL_CREATE_APPLE_PAY_PAYMENT,
  URL_OCP_SUBMIT_CARD_DETAILS,
  URL_REQUEST_APPLE_PAY_SESSION,
  URL_APPLE_PAY_EXPRESS_REQUEST_V2,
} from '@constants/endpointConstants';
import errorCodeConstants from '@constants/errorCodeConstants';
import paymentStatusConstants from '@constants/paymentStatusConstants';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import { isApps, isIosApp, isWeb } from '@redux/reducers/app/appReducer';
import { getSessionIdSelector } from '@redux/reducers/bff/bffSelector';
import { getInvalidApplePayBillingAddressResolverSelector } from '@redux/reducers/payment/paymentSelector';
import {
  countryCodeIsInternational,
  getBillingAddress,
  getFormattedAddressFromApplePay,
  mapAddressValidationErrorToApplePayErrors,
} from '@utils/address/addressHelpers';
import getBillingDetailsFormConfig from '@utils/form/configs/billingAddress';
import { shouldShowApplePayFeature } from '@utils/payment/isBasketApplePayEligible';
import { sendNewRelicCustomEvent, logToConsole } from '@utils/logging/logging-utils';
import { isEmptyObject } from '@utils/object';
import handleInconsistentOrderFormState from '@utils/orderform/handleInconsistentOrderFormState';
import monetateConstants from '@constants/monetateConstants';
import { dispatchMonetateEvent } from '@utils/analytics/trigger/monetateTrigger';

export const setCanMakeApplePayPayments = () => (dispatch, getState) => {
  if (!shouldShowApplePayFeature(getState())) return;

  dispatch({
    type: SET_CAN_MAKE_APPLE_PAY_PAYMENTS,
  });
};

export const setApplePayCardType =
  (cardType = '', express) =>
  async (dispatch, getState) => {
    const state = getState();
    const billingAddress = state.payment?.billingAddress;
    const nameOnCard = `${billingAddress.addressee?.firstName} ${billingAddress.addressee?.lastName}`;

    return dispatch({
      type: APPLE_PAY_SET_CARD_TYPE,
      request: (client) =>
        client({
          path: URL_CREATE_APPLE_PAY_PAYMENT,
          config: {
            method: 'PATCH',
            body: {
              cardType,
              nameOnCard,
              ...(express && { express }),
            },
          },
        }),
    });
  };

export const fixExpressCancelStubs = (express) => {
  if (express && env.isClientNonProd && window.PaymentWallet?.state === 'cancelled') {
    window.PaymentWallet.state = 'created';
  }
};

export const applePayOnCancel = () => async (dispatch, getState) => {
  const sessionId = getSessionIdSelector(getState());

  sendNewRelicCustomEvent('applePayCancelled', {
    sessionId,
  });
  dispatch({
    type: APPLE_PAY_CANCEL,
  });
  dispatch(initPaymentPage());

  const resolver = getInvalidApplePayBillingAddressResolverSelector(getState());
  return typeof resolver === 'function' ? resolver({}) : undefined;
};

export const resubmitApplePayBillingAddress = () => async (dispatch, getState) => {
  const billingAddress = getBillingAddress(getState());

  const updateBillingAddressResponse = await dispatch(
    updateBillingAddress({
      body: billingAddress,
      isApplePaySelected: true,
    }),
  );

  const invalidApplePayBillingAddressResolver = getInvalidApplePayBillingAddressResolverSelector(getState());
  if (updateBillingAddressResponse.type === `${UPDATE_BILLING_ADDRESS}.SUCCESS`) {
    return invalidApplePayBillingAddressResolver(billingAddress);
  }

  return invalidApplePayBillingAddressResolver({});
};

export const cancelApplePayPayment =
  ({ error = 'Unknown Error', express = false, errorIdentifier = '' } = {}) =>
  (dispatch, getState) => {
    const state = getState();

    logToConsole(error);
    sendNewRelicCustomEvent('applePayCancelled', {
      error,
      ...(errorIdentifier && { errorIdentifier }),
      sessionId: state?.bff?.sessionId,
    });

    dispatch({ type: APPLE_PAY_PAYMENT_CANCEL, express });

    if (express) dispatch(cleanupExpressBasket());

    fixExpressCancelStubs(express);

    if (!express) dispatch(initPaymentPage());
  };

export const failureApplePayPayment =
  ({ error = 'Unknown Error', express = false, errorIdentifier = '', shouldInitPaymentPage = false } = {}) =>
  (dispatch, getState) => {
    const state = getState();

    logToConsole(error);
    sendNewRelicCustomEvent('applePayFailed', {
      error,
      ...(errorIdentifier && { errorIdentifier }),
      sessionId: state?.bff?.sessionId,
    });
    dispatchMonetateEvent({ eventName: monetateConstants.PAYMENT.APPLE_PAY.FAILURE });
    dispatch({ type: APPLE_PAY_PAYMENT_FAILURE, express });
    if (express) dispatch(cleanupExpressBasket());

    fixExpressCancelStubs(express);

    if (shouldInitPaymentPage && !express) dispatch(initPaymentPage());
  };

const requestPaymentSession = (body, hostUrl, headers) => ({
  type: APPLE_PAY_REQUEST_PAYMENT_SESSION,
  request: (client) =>
    client({
      path: URL_REQUEST_APPLE_PAY_SESSION,
      config: {
        method: 'POST',
        headers,
        body,
      },
      hostUrl,
    }),
});

const submitApplePayPaymentData = (paymentId, body, hostUrl, headers) => ({
  type: APPLE_PAY_SUBMIT_PAYMENT_DATA,
  request: (client) =>
    client({
      path: URL_OCP_SUBMIT_CARD_DETAILS(paymentId),
      config: {
        method: 'PATCH',
        headers,
        body,
      },
      hostUrl,
    }),
});

const merchantSessionPromise = (event, dispatch, state, express) =>
  new Promise(async (resolve, reject) => {
    const host = state?.orderForm?.ocpIntegration?.baseUrl;
    const sessionId = state?.bff?.sessionId ?? '';
    const orderFormId = state?.orderForm?.id ?? '';
    const omsOrderId = state?.orderForm?.omsOrderId ?? '';
    const { displayName, initiative, initiativeContext, merchantIdentifier } =
      state?.config?.applePayConfiguration?.merchantValidationData ?? {};

    const body = {
      displayName,
      initiative,
      initiativeContext,
      merchantIdentifier,
      validationUrl: event.validationURL,
    };

    const headers = {
      JSESSIONID: sessionId,
      'JL-BASKET-ID': orderFormId,
      'JL-OMS-ORDER-ID': omsOrderId,
      'JL-CALLING-APP-V1': 'OCC',
      'Accept-Language': '*',
    };

    const postLocation = `${host}/${URL_REQUEST_APPLE_PAY_SESSION}`;

    const { type, result = {}, error = {} } = await dispatch(requestPaymentSession(body, host, headers));

    if (type === `${APPLE_PAY_REQUEST_PAYMENT_SESSION}.SUCCESS`) {
      sendNewRelicCustomEvent('applePaySessionResponse', {
        statusCode: 200,
        orderFormId,
        postLocation,
        sessionId,
      });
      resolve(result);
    } else {
      sendNewRelicCustomEvent('applePaySessionResponse', {
        ...error,
        orderFormId,
        postLocation,
        sessionId,
      });
      dispatch({ type: APPLE_PAY_PAYMENT_FAILURE, express });
      reject();
    }
  });

const submitApplePayPayment = (billingAddress, paymentResponse, express) => async (dispatch, getState) => {
  const shouldRequestBillingAddressFromApplePay = !isApps(getState());

  if (billingAddress) {
    const validateAddressResult = validate(
      {
        ...billingAddress.address,
        ...billingAddress.addressee,
        phoneNumber: billingAddress.phoneNumber,
      },
      {
        config: getBillingDetailsFormConfig({
          countryCode: billingAddress.address?.countryCode,
          actions: {},
          enableGBCounty: !!billingAddress.address?.countyStateOrProvince,
        }),
      },
    );

    if (!isEmptyObject(validateAddressResult)) {
      return {
        validateAddressResult,
      };
    }

    const { type } = await dispatch(
      updateBillingAddress({
        body: billingAddress,
        shouldRequestBillingAddressFromApplePay,
        isApplePaySelected: true,
      }),
    );

    if (type === `${UPDATE_BILLING_ADDRESS}.FAILED`) {
      return {};
    }
  }

  const createApplePayPaymentResponse = await dispatch({
    type: APPLE_PAY_PAYMENT,
    request: (client) =>
      client({
        path: URL_CREATE_APPLE_PAY_PAYMENT,
        config: { method: 'POST' },
      }),
  });

  await dispatch(triggerPlaceOrderAndPayAnalytics(createApplePayPaymentResponse?.result?.id));

  if (!isApps(getState()) && createApplePayPaymentResponse.type === `${APPLE_PAY_PAYMENT}.SUCCESS`) {
    const setCardTypeResponse = await dispatch(
      setApplePayCardType(paymentResponse?.details?.token?.paymentMethod?.network, express),
    );
    if (setCardTypeResponse.type === `${APPLE_PAY_SET_CARD_TYPE}.FAILED`) {
      return setCardTypeResponse;
    }
  }

  return createApplePayPaymentResponse;
};

export const triggerOpenApplePayPaymentSheetEvent =
  ({ express = false } = {}) =>
  async (dispatch, getState) => {
    dispatch(
      triggerAnalyticsEvent(
        express ? ANALYTICS_OPEN_APPLE_PAY_EXPRESS_PAYMENT_SHEET : ANALYTICS_OPEN_APPLE_PAY_PAYMENT_SHEET,
        {
          payment: {
            payment: {
              ...getState().analytics?.analyticsData?.checkout?.payment?.order,
            },
          },
        },
      ),
    );
  };

const submitApplePayToken = (token) => async (dispatch, getState) => {
  const state = getState();
  const paymentId = state?.payment?.ocpPaymentId;
  const host = state?.orderForm?.ocpIntegration?.baseUrl;
  const sessionId = state?.bff?.sessionId ?? '';
  const orderFormId = state?.orderForm?.id ?? '';
  const omsOrderId = state?.orderForm?.omsOrderId ?? '';
  const body = {
    method: 'applePay',
    applePayData: window.btoa(JSON.stringify(token)),
  };

  const headers = {
    JSESSIONID: sessionId,
    'JL-BASKET-ID': orderFormId,
    'JL-OMS-ORDER-ID': omsOrderId,
    'JL-CALLING-APP-V1': 'OCC',
    Accept: 'application/vnd.jl-payments-v3+json',
    'Content-Type': 'application/vnd.jl-payments-v3+json',
    'Accept-Language': '*',
  };

  const submitAction = await dispatch(submitApplePayPaymentData(paymentId, body, host, headers));

  const {
    type,
    error: { code, authenticationStatus: authenticationStatusOnError } = {},
    result: { status, authenticationStatus: authenticationStatusOnSuccess } = {},
  } = submitAction;

  if (type === `${APPLE_PAY_SUBMIT_PAYMENT_DATA}.SUCCESS`) {
    dispatch(triggerTransactionStatusAnalytics(status, { authenticationStatus: authenticationStatusOnSuccess }));
  }

  if (type === `${APPLE_PAY_SUBMIT_PAYMENT_DATA}.FAILED`) {
    dispatch(
      triggerTransactionStatusAnalytics('FAILED', {
        errorCode: code,
        authenticationStatus: authenticationStatusOnError,
      }),
    );
  }

  return submitAction;
};

export const submitApplePayTokenAndOrder = (token) => async (dispatch) => {
  const submitApplePayTokenResponse = await dispatch(submitApplePayToken(token));
  const { type, result: { status } = {} } = submitApplePayTokenResponse;

  if (type === `${APPLE_PAY_SUBMIT_PAYMENT_DATA}.SUCCESS` && status === paymentStatusConstants.PENDING_AUTHORISATION) {
    dispatch(setPaymentProcessing(false));
    dispatch(submitOrderAndRedirect());
  } else {
    dispatch(
      failureApplePayPayment({
        error: submitApplePayTokenResponse,
        errorIdentifier: 'Failed in submitApplePayTokenAndOrder',
      }),
    );
  }
};

export const handlePaymentResponse =
  ({ response, isResubmitting = false, express } = {}) =>
  async (dispatch, getState) => {
    const applePayPaymentRequest = response || getState().payment.applePayPaymentRequest;
    const billingContact = applePayPaymentRequest?.details?.billingContact;
    const payerPhone = applePayPaymentRequest?.payerPhone;

    if (response) {
      if (express) {
        const shippingContact = applePayPaymentRequest?.details?.shippingContact;

        const handleApplePayExpressResponse = await dispatch(
          handleApplePayExpress({
            shippingContact,
            payerPhone,
            selectedShippingOptionIdentifier: getState().delivery?.applePayEnhancedShippingIdentifier,
          }),
        );

        const { response: { type: handleApplePayExpressResponseType, validateAddressResult } = {} } =
          handleApplePayExpressResponse;

        if (handleApplePayExpressResponseType === EXPRESS_PAYMENTS_VALIDATION_FAILED) {
          if (validateAddressResult) {
            const errors = mapAddressValidationErrorToApplePayErrors({
              validationErrors: validateAddressResult,
              errorKey: 'shippingContactInvalid',
            });

            if (!isEmptyObject(errors?.paymentMethod)) {
              applePayPaymentRequest
                .retry(errors)
                .then(() => {
                  dispatch(handlePaymentResponse({ response: applePayPaymentRequest, express }));
                })
                .catch((error) => {
                  applePayPaymentRequest.complete('fail');
                  dispatch(
                    failureApplePayPayment({
                      error,
                      express,
                      errorIdentifier:
                        'Express Payment Validation Failed With validateAddressResult - shippingContactInvalid After Retry',
                      shouldInitPaymentPage: true,
                    }),
                  );
                });
            }
          } else {
            dispatch(
              failureApplePayPayment({
                error: handleApplePayExpressResponse,
                express,
                errorIdentifier: 'Express Payment Validation Failed Without validateAddressResult',
                shouldInitPaymentPage: true,
              }),
            );
            applePayPaymentRequest.complete('fail');
          }

          return;
        }

        if (handleApplePayExpressResponse?.type.includes(EXPRESS_PAYMENTS_FAILED)) {
          if (
            handleApplePayExpressResponse?.response?.error?.code === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE
          ) {
            dispatch(handleInconsistentOrderFormState());
          }

          return;
        }
      }

      await dispatch({
        type: SET_APPLE_PAY_PAYMENT_REQUEST,
        paymentRequest: response,
      });
    }

    const shouldRequestBillingAddressFromApplePay = !isApps(getState());

    let billingAddress;

    if (shouldRequestBillingAddressFromApplePay && !isEmptyObject(billingContact) && payerPhone && !isResubmitting) {
      billingAddress = getFormattedAddressFromApplePay(billingContact, payerPhone);
    } else {
      billingAddress = getBillingAddress(getState());
    }

    if (!billingAddress || isEmptyObject(billingAddress)) {
      const sessionId = getState()?.bff?.sessionId ?? '';
      const orderFormId = getState()?.orderForm?.id ?? '';

      sendNewRelicCustomEvent('applePayBillingAddressMissing', {
        orderFormId,
        sessionId,
        isApps: isApps(getState()),
      });

      dispatch({
        type: APPLE_PAY_BILLING_ADDRESS_MISSING,
        error: {
          code: errorCodeConstants.CLIENT_APPLE_PAY_BILLING_ADDRESS_MISSING,
        },
      });

      if (express) {
        logToConsole(APPLE_PAY_BILLING_ADDRESS_MISSING);
        dispatch(handleExpressFailure(response));
      }
      return;
    }

    const submitApplePayPaymentResponse = await dispatch(
      submitApplePayPayment(billingAddress, applePayPaymentRequest, express),
    );

    if (shouldRequestBillingAddressFromApplePay) {
      const errors = mapAddressValidationErrorToApplePayErrors({
        validationErrors: submitApplePayPaymentResponse.validateAddressResult,
        errorKey: 'billingContactInvalid',
      });

      if (!isEmptyObject(errors?.paymentMethod)) {
        applePayPaymentRequest
          .retry(errors)
          .then(() => {
            dispatch(handlePaymentResponse({ response: applePayPaymentRequest }));
          })
          .catch((error) => {
            dispatch(
              failureApplePayPayment({
                error,
                express,
                errorIdentifier:
                  'Failed after Retry when shouldRequestBillingAddressFromApplePay - billingContactInvalid',
                shouldInitPaymentPage: true,
              }),
            );
          });

        return;
      }
    }

    const { type } = submitApplePayPaymentResponse;

    if (type === `${APPLE_PAY_SET_CARD_TYPE}.FAILED`) {
      dispatch(
        failureApplePayPayment({
          error: submitApplePayPaymentResponse,
          express,
          errorIdentifier: 'Failed in handlePaymentResponse as APPLE_PAY_SET_CARD_TYPE status is FAILED',
        }),
      );
      applePayPaymentRequest.complete('fail');
      return;
    }

    if (type === `${APPLE_PAY_PAYMENT}.SUCCESS`) {
      const { type, result: { status } = {} } = await dispatch(
        submitApplePayToken(applePayPaymentRequest.details.token),
      );

      if (
        type === `${APPLE_PAY_SUBMIT_PAYMENT_DATA}.SUCCESS` &&
        status === paymentStatusConstants.PENDING_AUTHORISATION
      ) {
        await applePayPaymentRequest.complete('success');
        dispatch(setPaymentProcessing(false));
        dispatch(submitOrderAndRedirect({ express }));
        return;
      }
    }

    applePayPaymentRequest.complete('fail');
    dispatch(failureApplePayPayment({ express, errorIdentifier: 'Failed in handlePaymentResponse as uncaught error' }));
  };

const expressPaymentRequestPayload = async (state, dispatch) => {
  const applePayExpressPaymentResponse = await dispatch(getApplePayExpressPaymentRequest());

  const { config: { applePayConfiguration: { applePayMethod } = {} } = {} } = state;

  return {
    applePayMethod,
    details: {
      total: applePayExpressPaymentResponse?.result?.total,
      modifiers: applePayExpressPaymentResponse?.result?.modifiers,
    },
    options: {
      requestPayerName: true,
      requestBillingAddress: true,
      requestPayerEmail: false,
      requestPayerPhone: true,
      requestShipping: true,
    },
  };
};

const paymentRequestPayload = (state, express, dispatch) => {
  if (express) return expressPaymentRequestPayload(state, dispatch);

  return {
    applePayMethod: state?.config?.applePayConfiguration?.applePayMethod,
    details: {
      total: {
        label: 'John Lewis',
        amount: {
          currency: 'GBP',
          value: state?.orderForm?.amounts?.outstandingBalanceRaw,
        },
      },
    },
    options: {
      requestPayerName: false,
      requestBillingAddress: true,
      requestPayerEmail: false,
      requestPayerPhone: true,
      requestShipping: false,
    },
  };
};

export const onShippingAddressChangeHandler =
  ({ details, event, express, paymentRequest }) =>
  async (dispatch, getState) => {
    event.updateWith(
      new Promise(async (resolve, reject) => {
        const state = getState();

        if (express) {
          const validateExpressAddress = (address) => {
            if (!address?.city?.trim()) return 'Missing city';
            if (!address?.country?.trim()) return 'Missing country';
            if (!address?.postalCode?.trim()) return 'Missing postcode';
            if (countryCodeIsInternational(address?.country)) return 'We only ship to the UK';
            return null;
          };

          const error = validateExpressAddress(paymentRequest?.shippingAddress);
          if (error) {
            resolve({ error });
            return;
          }

          const { city, country, postalCode } = paymentRequest?.shippingAddress || {};

          const partialAddress = {
            townOrCity: city,
            countryCode: country,
            postcode: `${postalCode}1AA`,
          };

          const setPartialDeliveryAddressResponse = await dispatch(setPartialDeliveryAddress(partialAddress));

          if (setPartialDeliveryAddressResponse?.type?.includes('FAILED')) {
            resolve({ error: 'Setting partial delivery address failed' });
            return;
          }

          const paymentRequestPayloadResponse = await paymentRequestPayload(state, express, dispatch);
          const availableShippingMethods =
            paymentRequestPayloadResponse?.details?.modifiers?.[0]?.data?.additionalShippingMethods || [];

          if (availableShippingMethods.length === 0) {
            dispatch(handleExpressFailure());
            if (paymentRequest.abort) paymentRequest.abort();
            reject();
            return;
          }

          resolve(paymentRequestPayloadResponse?.details);
        } else {
          resolve(details);
        }
      }),
    );
  };

export const onShippingOptionChangeHandler =
  ({ details, event, express, paymentRequest }) =>
  async (dispatch, getState) => {
    event.updateWith(
      new Promise(async (resolve, reject) => {
        const state = getState();

        if (express) {
          const {
            delivery: { applePayExpressConfig, applePayEnhancedShippingIdentifier } = {},
            orderForm: { amounts: { outstandingBalanceRaw } = {} } = {},
          } = state;

          const availableShippingMethods = applePayExpressConfig?.modifiers?.[0]?.data?.additionalShippingMethods || [];
          if (availableShippingMethods.length === 0) {
            dispatch(handleExpressFailure());
            if (paymentRequest.abort) paymentRequest.abort();
            reject();
            return;
          }

          const selectedShippingMethod =
            availableShippingMethods.find((method) => method?.identifier === paymentRequest.shippingOption) ||
            availableShippingMethods[0];

          if (applePayEnhancedShippingIdentifier !== selectedShippingMethod?.identifier) {
            await dispatch({
              type: SET_APPLE_PAY_ENHANCED_SHIPPING_IDENTIFIER,
              applePayEnhancedShippingIdentifier: selectedShippingMethod?.identifier,
            });
          }

          const updatedValue = (Number(outstandingBalanceRaw) + Number(selectedShippingMethod?.amount)).toFixed(2);

          // set selected delivery method from the identifier provided by apple pay
          const updatedShippingMethods = availableShippingMethods.map((method) => ({
            ...method,
            selected: method?.identifier === selectedShippingMethod.identifier,
          }));

          const updateObject = {
            total: {
              ...applePayExpressConfig?.total,
              amount: {
                ...applePayExpressConfig?.total?.amount,
                value: updatedValue,
              },
            },
            modifiers: [
              {
                supportedMethods: applePayExpressConfig?.modifiers[0]?.supportedMethods,
                data: {
                  additionalShippingMethods: updatedShippingMethods,
                },
              },
            ],
          };

          resolve(updateObject);
        } else {
          resolve(details?.total);
        }
      }),
    );
  };

const applePayPaymentInitOnWeb =
  ({ express } = {}) =>
  async (dispatch, getState) => {
    try {
      const state = getState();
      const { applePayMethod, details, options } = await paymentRequestPayload(state, express, dispatch);

      const paymentRequest = new window.PaymentRequest([applePayMethod], details, options);

      // allow access to the paymentRequest from PaymentWalletStubsModal
      if (env.isClientNonProd) window.mockPaymentRequest = paymentRequest;

      paymentRequest.onshippingaddresschange = async (event) =>
        dispatch(
          onShippingAddressChangeHandler({
            event,
            details,
            paymentRequest,
            express,
          }),
        );

      paymentRequest.onshippingoptionchange = async (event) =>
        dispatch(
          onShippingOptionChangeHandler({
            event,
            details,
            paymentRequest,
            express,
          }),
        );

      paymentRequest.onmerchantvalidation = (event) => {
        event.complete(merchantSessionPromise(event, dispatch, state, express));
      };

      dispatch(triggerOpenApplePayPaymentSheetEvent({ express }));

      await paymentRequest
        .show()
        .then(async (response) => {
          await dispatch(
            handlePaymentResponse({
              response,
              express,
            }),
          );
          if (window.mockPaymentRequest) delete window.mockPaymentRequest;
        })
        .catch((error) => {
          // Exiting/Cancelling the payment request flow falls into this catch block so we use the cancelApplePayPayment action
          dispatch(
            cancelApplePayPayment({
              error,
              express,
              errorIdentifier: `Payment Request Cancelled on Web during payment handling: Express - ${express}`,
            }),
          );
        });
    } catch (error) {
      dispatch(
        failureApplePayPayment({
          error,
          express,
          errorIdentifier: `Payment Request Failure on Web during payment initialization: Express - ${express}`,
        }),
      );
    }
  };

const applePayPaymentInitOnIosApp = () => async (dispatch, getState) => {
  try {
    const billingAddress = getBillingAddress(getState());
    const submitApplePayPaymentResponse = await dispatch(submitApplePayPayment(billingAddress));
    if (submitApplePayPaymentResponse?.type === `${APPLE_PAY_PAYMENT}.SUCCESS`) {
      window.webkit.messageHandlers.sendApplePayPaymentRequest.postMessage(paymentRequestPayload(getState()));
      dispatch(triggerOpenApplePayPaymentSheetEvent());
    } else {
      dispatch(
        failureApplePayPayment({
          error: submitApplePayPaymentResponse,
          errorIdentifier: 'Failed in applePayPaymentInitOnIosApp as APPLE_PAY_PAYMENT not SUCCESS',
        }),
      );
    }
  } catch (error) {
    dispatch(
      failureApplePayPayment({
        error,
        errorIdentifier: 'Payment Request Failure on iOS App during payment initialization',
      }),
    );
  }
};

export const applePayPaymentInit = () => async (dispatch, getState) => {
  const state = getState();

  if (isWeb(state)) {
    await dispatch(applePayPaymentInitOnWeb());
  } else if (isIosApp(state)) {
    await dispatch(applePayPaymentInitOnIosApp());
  }
};

export const applePayExpress = () => async (dispatch) => {
  dispatch(setPaymentProcessing(true));
  dispatch(applePayPaymentInitOnWeb({ express: true }));
};

export const getApplePayExpressPaymentRequest = () => async (dispatch) => {
  return dispatch({
    type: GET_APPLE_PAY_EXPRESS_PAYMENT_REQUEST,
    request: (client) =>
      client({
        path: URL_APPLE_PAY_EXPRESS_REQUEST_V2,
        config: { method: 'GET' },
      }),
  });
};
